/**
 * Deep-link URL schema for QR code navigation
 * 
 * Format: /qr/{wellId}?focusField={fieldName}
 * 
 * Examples:
 * - /qr/123 → Opens today's log for well 123
 * - /qr/123?focusField=hours → Opens today's log for well 123 and scrolls to hours field
 * - /qr/123?focusField=measuredInchesCl2 → Opens today's log and scrolls to chlorine gauge
 */

export type FocusField = 
  | "date"
  | "time"
  | "operator"
  | "meterReadStart"
  | "meterReadEnd"
  | "gpm"
  | "hours"
  | "remarks"
  | "measuredInchesCl2"
  | "inchesUsedCl2"
  | "lbsCl2"
  | "freeResidualPpm"
  | "psi"
  | "staticValue"
  | "pumpingValue"
  | "topOffCl2"
  | "residual";

export interface DeepLinkParams {
  wellId: number;
  focusField?: FocusField;
}

export function buildDeepLinkUrl(baseUrl: string, params: DeepLinkParams): string {
  const url = new URL(`/qr/${params.wellId}`, baseUrl);
  if (params.focusField) {
    url.searchParams.set('focusField', params.focusField);
  }
  return url.toString();
}

export function parseDeepLinkUrl(pathname: string, searchParams: URLSearchParams): DeepLinkParams | null {
  const match = pathname.match(/^\/qr\/(\d+)$/);
  if (!match) return null;
  
  const wellId = parseInt(match[1], 10);
  const focusField = searchParams.get('focusField') as FocusField | null;
  
  return {
    wellId,
    focusField: focusField || undefined,
  };
}

// Field display names for QR code labels
export const FIELD_LABELS: Record<FocusField, string> = {
  date: "Date",
  time: "Time",
  operator: "Operator Initial",
  meterReadStart: "Meter Read Start",
  meterReadEnd: "Meter Read End",
  gpm: "GPM",
  hours: "Hour Meter",
  remarks: "Remarks",
  measuredInchesCl2: "Chlorine Gauge (Measured Inches)",
  inchesUsedCl2: "Chlorine Used",
  lbsCl2: "Chlorine (lbs)",
  freeResidualPpm: "Free Chlorine Residual",
  psi: "Pressure (PSI)",
  staticValue: "Static Water Level",
  pumpingValue: "Pumping Water Level",
  topOffCl2: "Chlorine Top-Off",
  residual: "Tower Residual",
};

// Common instrument QR code presets
export interface InstrumentPreset {
  name: string;
  focusField: FocusField;
  description: string;
}

export const INSTRUMENT_PRESETS: InstrumentPreset[] = [
  {
    name: "Hour Meter",
    focusField: "hours",
    description: "Scan to log hour meter reading",
  },
  {
    name: "Chlorine Gauge",
    focusField: "measuredInchesCl2",
    description: "Scan to log chlorine tank level",
  },
  {
    name: "Pressure Gauge",
    focusField: "psi",
    description: "Scan to log pressure reading",
  },
  {
    name: "Water Meter",
    focusField: "meterReadEnd",
    description: "Scan to log water meter reading",
  },
  {
    name: "Tower Residual",
    focusField: "residual",
    description: "Scan to log tower residual measurement",
  },
];
